import React, { Context, JSX, ReactNode, createContext, useContext, useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { CruisesContract as API } from '@luxuryescapes/contract-svc-cruise';

import useAppNavigation from '~/hooks/useAppNavigation';

import offerService, { OfferResponse } from '~/services/cruises/OfferService';

import { parseSearchString } from '~/utils/url';

interface Filter {
  name?: string;
  status?: API.OfferStatus | 'ALL';
  cruiseLine?: string;
  departureDate?: string;
  shipName?: string;
  marketName?: string;
  rateCode?: string;
  id?: string;
  departureExternalId?: string;
}

type Offer = OfferResponse['result'];

interface CruiseOffersData {
  page: number;
  total: number;
  cruisesPerPage: number;
  offers: Array<API.OfferSummary>;
  sortedOffers: Array<Offer>;
  loadingOffers: boolean;
  setFilter: (value: Filter) => void;
  filter: Filter;
  paginate: (page: number) => void;
}

interface FetchOffer {
  result: Array<API.OfferSummary>;
  loading: boolean;
}

interface CruiseOffersProviderProps {
  children?: ReactNode;
}

export const CruiseOffersContext: Context<CruiseOffersData> = createContext({} as CruiseOffersData);

export function CruiseOffersProvider({ children }: CruiseOffersProviderProps): JSX.Element {
  const CRUISES_PER_PAGE = 15;
  const { search } = useLocation();
  const { push: setQueryString } = useAppNavigation();

  const [filter, setFilter] = useState<Filter>({ status: 'ALL' });
  const [page, setPage] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [offers, setOffers] = useState<FetchOffer>({
    result: [],
    loading: false,
  });
  const [sortedOffers, setSortedOffers] = useState<Array<Offer>>([]);

  const getOffersList = async () => {
    setOffers((prev) => ({ ...prev, loading: true }));
    const skip = page * CRUISES_PER_PAGE;

    const { result, total } = await offerService.getListWithPagination({
      ...(filter.name && { name: filter.name }),
      ...(filter.cruiseLine && { cruiseLine: filter.cruiseLine }),
      ...(filter.status !== 'ALL' && { status: filter.status }),
      ...(filter.shipName && { shipName: filter.shipName }),
      ...(filter.departureDate && { departureDate: filter.departureDate }),
      ...(filter.marketName && { marketName: filter.marketName }),
      ...(filter.rateCode && { rateCode: filter.rateCode }),
      ...(filter.id && { id: filter.id }),
      ...(filter.departureExternalId && { departureExternalId: filter.departureExternalId }),
      skip,
      take: CRUISES_PER_PAGE,
    });

    setTotal(total);
    setOffers({ loading: false, result });
  };

  const getOffersOrdered = async () => {
    setOffers((prev) => ({ ...prev, loading: true }));

    try {
      const { result } = await offerService.getSortedOffers();

      if (!result) {
        return setSortedOffers([]);
      }

      const getOffers = result.map(({ offer_id }) => {
        return offerService.getById(offer_id);
      });

      const offers = await Promise.all(getOffers);

      const nonExpiredOffers = offers.filter((offer) => Object.entries(offer.result.departures ?? {}).length > 0);

      setSortedOffers(nonExpiredOffers.map((response) => response.result));
    } catch (error) {
      setSortedOffers([]);
    }
  };

  const setCurrentPage = (): void => {
    const queryString = parseSearchString(search);
    const pageToGo = parseInt(queryString?.page as string);
    if (pageToGo) {
      setPage(pageToGo);
    }
  };

  const paginate = (page: number): void => {
    const queryString = new URLSearchParams({ page: page.toString() });
    setQueryString({ search: queryString.toString() });
    setPage(page);
  };

  useEffect(() => {
    setCurrentPage();
  }, []);

  useEffect(() => {
    getOffersList();
  }, [page, filter]);

  useEffect(() => {
    getOffersOrdered();
  }, []);

  return (
    <CruiseOffersContext.Provider
      value={{
        page,
        total,
        setFilter,
        paginate,
        filter,
        offers: offers.result,
        loadingOffers: offers.loading,
        cruisesPerPage: CRUISES_PER_PAGE,
        sortedOffers: sortedOffers,
      }}
    >
      {children}
    </CruiseOffersContext.Provider>
  );
}

export const useCruiseOffers = (): CruiseOffersData => useContext(CruiseOffersContext);
